import { useEffect, useRef, useState } from "react";
import "./App.css";
import { createTheme, ThemeProvider } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import Routes from "./routes/Routes";
import { FullLoader } from "@gse/react-components-dg-qa";
import { decodeToken, decodeTokenData } from "./utils/general/utils";
import { SnackbarProvider } from "notistack";
import { authSelector, setAuth, setTokens } from "./store/slices/AuthSlice";
import { setRol, CommonSelector } from "./store/slices/CommonSlice";
import { getPersonalizationAC } from "./store/action-creators/CompanyActionCreators";
import { setPersonalization } from "./utils/general/personalization";
import { Roles } from "./utils/types/const";
import { ValidateVersionAC } from "./store/action-creators/AuthActionCreators";
import {
  NotifyActualizationSocket,
  finalizeAndRevokeSession,
} from "./services/AuthServices";
import CloseButton from "./components/Snackbar/CloseButton";
import { makeStyles } from "@material-ui/core/styles";
import { DataToken } from "./store/types";

const useStyles = makeStyles({
  variantError: {
    fontSize: "1.1rem",
  },
  variantSuccess: {
    fontSize: "1.2rem",
  },
  variantWarning: {
    fontSize: "1.2rem",
  },
  variantInfo: {
    fontSize: "1.2rem",
  },
});

function App() {
  // state REDUX
  const { isAuth, isLoading, tokens, updateDesktop } =
    useSelector(authSelector);
  const { rol } = useSelector(CommonSelector);
  const [theme, setTheme] = useState(createTheme());
  const dispatch = useDispatch();
  const scriptRef = useRef<any>(null);
  const classes = useStyles();

  const validateInitialSession = () => {
    try {
      const encryptedToken = window.location.pathname.substring(1);
      if (encryptedToken.length > 50) {
        sessionStorage.removeItem("jwtToken");
        const token: any = decodeTokenData(encryptedToken);
        const tokenDecode = decodeToken(token.token);
        sessionStorage.setItem("jwtToken", token.token);
        sessionStorage.setItem(
          "typeUser",
          tokenDecode?.tenant === "fna" ? "1" : "2"
        );
        sessionStorage.setItem("refreshToken", token.refreshToken);
        const userRole = tokenDecode?.BIOMETRIA_ROLE;
        const userRoleToURL: Record<string, string> = {
          "BIOMETRIA RNEC": "/companylist",
          "BIOMETRIA CAPTURADOR MOVIL": "/fingerprint",
          "BIOMETRIA INTEGRACION": "/fingerprint",
          "BIOMETRIA CAPTURA MOVIL": "/fingerprint",
        };
        window.location.href = userRoleToURL[userRole ?? ""] || "/dashboard";
      } else {
        finalizeAndRevokeSession();
      }
    } catch (error) {
      window.history.back();
    }
  };

  // React hooks
  useEffect(() => {
    if (!!tokens && !!tokens.result?.token) {
      setPersonalization(null, setTheme);
      dispatch(getPersonalizationAC(tokens.result.token, setTheme));
    }
  }, [tokens]);

  useEffect(() => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    const refreshToken = sessionStorage.getItem("refreshToken");
    let tokenDecode: DataToken | undefined;

    if (jwtToken && refreshToken && !isAuth) {
      tokenDecode = decodeToken(jwtToken);
      dispatch(setAuth(true));
      dispatch(
        setTokens({
          result: { token: jwtToken, refreshToken: refreshToken },
          error: {},
        })
      );
      dispatch(
        setRol(tokenDecode === undefined ? "" : tokenDecode?.BIOMETRIA_ROLE)
      );

      const navigateURL = window.location.pathname.substring(1);

      if (!navigateURL) {
        const userRole = tokenDecode?.BIOMETRIA_ROLE;
        const userRoleToURL: Record<string, string> = {
          "BIOMETRIA RNEC": "/companylist",
          "BIOMETRIA CAPTURADOR MOVIL": "/fingerprint",
          "BIOMETRIA INTEGRACION": "/fingerprint",
          "BIOMETRIA CAPTURA MOVIL": "/fingerprint",
        };
        window.location.href = userRoleToURL[userRole ?? ""] || "/dashboard";
      }
    } else if (!isAuth && !jwtToken) {
      validateInitialSession();
    } else {
      finalizeAndRevokeSession();
    }
  }, []);

  //crear etiqueta script e insertar un texto que lance el popstate para bloquear ir hacia atras con el navegador solo cuando el rol sea RNEC
  useEffect(() => {
    const script = document.createElement("script");
    //verificar rol capturador y enviar el servicio de consulta de version de software Biometrics Desktop
    if (
      rol === Roles.BIOMETRIA_CAPTURADOR ||
      rol === Roles.BIOMETRIA_CAPTURADOR_MOVIL ||
      rol === Roles.BIOMETRIA_CAPTURA_MOVIL ||
      rol === Roles.BIOMETRIA_INTEGRACION
    ) {
      dispatch(ValidateVersionAC());
    }

    if (rol === Roles.BIOMETRIA_RNEC) {
      script.innerHTML = `
      window.history.pushState(null, "", window.location.href);
      window.onpopstate = function () {
      window.history.go(1);
      };`;
    }

    script.type = "text/javascript";
    scriptRef.current.appendChild(script);
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      scriptRef.current.removeChild(script);
    };
  }, [rol]);

  /* effect que espera la informacion que se almacena despues de consultar las actualizaciones del software de
  Biometrics Desktop y si tiene version pendiente conectar al socket de actualizacion de software */
  useEffect(() => {
    async function fetchData() {
      if (updateDesktop?.Update) {
        await NotifyActualizationSocket(updateDesktop);
      }
    }
    fetchData();
  }, [updateDesktop]);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        maxSnack={3}
        action={(key) => <CloseButton snackbarKey={key} />}
        classes={{
          variantError: classes.variantError,
          variantSuccess: classes.variantSuccess,
          variantInfo: classes.variantInfo,
          variantWarning: classes.variantWarning,
        }}
        hideIconVariant
      >
        {!isAuth ? (
          <FullLoader open={true} viewLoader={true} />
        ) : (
          <>
            <Routes />
            <FullLoader open={isLoading} viewLoader={true} />
          </>
        )}
      </SnackbarProvider>
      <div ref={scriptRef}></div>
    </ThemeProvider>
  );
}

export default App;
